import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/StatusPill";
import { ClientWorldMap } from "@/components/ClientWorldMap";
import { LinkedAccountsPanel } from "@/components/LinkedAccountsPanel";
import { api } from "@/lib/api";
import { langLabel, langFlag } from "@/lib/langLabel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Minus, Shield, User as UserIcon, Search, Coins, Users as UsersIcon,
  Euro, Activity, X, RotateCcw, FileStack, Receipt, Gift, Calendar, Loader2, Building2, BadgeCheck, ShieldAlert, Zap, Lock,
} from "lucide-react";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const initials = (n) => (n || "?").trim()[0]?.toUpperCase() || "?";

const RoleBadge = ({ role }) => (
  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${role === "admin" ? "border-aurora-purple/50 text-aurora-purple bg-aurora-purple/5" : "border-white/15 text-zinc-400"}`}>
    {role === "admin" ? "Admin" : "Client"}
  </span>
);

const AccountBadge = ({ accountType, vatValid, vatNumber, companyName }) => {
  if (accountType === "b2b") {
    return (
      <span
        data-testid="account-badge-b2b"
        title={`${companyName || "Professionnel"}${vatNumber ? " · " + vatNumber : ""}`}
        className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border inline-flex items-center gap-1 shrink-0 ${vatValid ? "border-signal-success/40 text-signal-success bg-signal-success/5" : "border-aurora-cyan/40 text-aurora-cyan bg-aurora-cyan/5"}`}
      >
        {vatValid ? <BadgeCheck className="w-2.5 h-2.5" /> : <Building2 className="w-2.5 h-2.5" />}
        {vatValid ? "B2B · TVA ✓" : "B2B"}
      </span>
    );
  }
  if (accountType === "b2c") {
    return (
      <span data-testid="account-badge-b2c" className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-white/10 text-zinc-500 shrink-0">
        B2C
      </span>
    );
  }
  return null;
};

const SIGNAL_LABELS = { device: "même appareil", email_alias: "alias email", ip: "même IP" };

const AbuseBadge = ({ user }) => {
  const flagged = user.abuse_flag;
  const linkedDev = user.linked_by_device || 0;
  const linkedIp = user.linked_by_ip || 0;
  if (!flagged && linkedDev === 0 && linkedIp === 0) return null;
  const signals = (user.abuse_signals || []).map((s) => SIGNAL_LABELS[s] || s);
  const parts = [];
  if (signals.length) parts.push(`Signaux à l'inscription : ${signals.join(", ")}`);
  if (linkedDev) parts.push(`${linkedDev} compte(s) lié(s) par appareil`);
  if (linkedIp) parts.push(`${linkedIp} compte(s) lié(s) par IP`);
  return (
    <span
      data-testid={`abuse-badge-${user.user_id}`}
      title={parts.join(" · ")}
      className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-signal-error/40 text-signal-error bg-signal-error/5 inline-flex items-center gap-1 shrink-0"
    >
      <ShieldAlert className="w-2.5 h-2.5" />
      {flagged ? "Multi-comptes" : `Lié ×${linkedDev + linkedIp}`}
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="glass rounded-md p-4">
    <Icon className="w-4 h-4 mb-2" style={{ color }} />
    <div className="font-display text-2xl font-bold" style={{ color }}>{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
  </div>
);

const ClientDetailModal = ({ userId, open, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditInput, setCreditInput] = useState("");
  const [refundCredits, setRefundCredits] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [slaveTool, setSlaveTool] = useState("");
  const [masterLabel, setMasterLabel] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/admin/users/${userId}`).then(({ data }) => {
      setD(data);
      setSlaveTool(data?.user?.slave_tool || "");
      setMasterLabel(data?.user?.master_label || "");
    }).finally(() => setLoading(false));
  };
  useEffect(() => { if (open && userId) { setD(null); setCreditInput(""); setRefundCredits(""); setRefundReason(""); load(); } }, [open, userId]);

  const adjust = async () => {
    const delta = parseInt(creditInput, 10);
    if (!delta) { toast.error("Saisissez un nombre (ex: 10 ou -5)"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { credits_delta: delta });
      toast.success(`${delta > 0 ? "+" : ""}${delta} crédits appliqués`);
      setCreditInput(""); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  const refund = async () => {
    const credits = parseInt(refundCredits, 10);
    if (!credits || credits <= 0) { toast.error("Nombre de crédits à rembourser invalide"); return; }
    if (!refundReason.trim()) { toast.error("Indiquez le motif du remboursement"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/refund`, { credits, reason: refundReason });
      toast.success(`Remboursé · +${credits} crédits (solde : ${data.credits})`);
      setRefundCredits(""); setRefundReason(""); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  const stripeRefund = async (sessionId, amount) => {
    if (!window.confirm(`Rembourser ${amount}€ sur la carte du client ?\n\n⚠️ Action IRRÉVERSIBLE — argent réel remboursé via Stripe.`)) return;
    const reason = window.prompt("Motif du remboursement (optionnel) :") || "";
    setBusy(true);
    try {
      await api.post(`/admin/transactions/${sessionId}/stripe_refund`, { reason });
      toast.success(`Remboursement Stripe effectué · ${amount}€`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du remboursement Stripe"); }
    setBusy(false);
  };

  const toggleRole = async () => {
    const next = d.user.role === "admin" ? "client" : "admin";
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { role: next });
      toast.success("Rôle mis à jour");
      load(); onChanged?.();
    } catch (e) { toast.error("Erreur"); }
    setBusy(false);
  };

  const togglePriority = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { priority_support: !d.user.priority_support });
      toast.success(!d.user.priority_support ? "Support Express activé" : "Support Express retiré");
      load(); onChanged?.();
    } catch (e) { toast.error("Erreur"); }
    setBusy(false);
  };

  const toggleSlave = async () => {
    const next = !d.user.is_slave;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, {
        is_slave: next,
        slave_tool: next ? (slaveTool || "dimsport") : "",
        master_label: next ? masterLabel : "",
      });
      toast.success(next ? "Mode Slave activé (fichiers cryptés → file manuelle)" : "Mode Slave désactivé");
      load(); onChanged?.();
    } catch (e) { toast.error("Erreur"); }
    setBusy(false);
  };

  const saveSlaveConfig = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { slave_tool: slaveTool, master_label: masterLabel });
      toast.success("Configuration Slave enregistrée");
      load(); onChanged?.();
    } catch (e) { toast.error("Erreur"); }
    setBusy(false);
  };

  const u = d?.user;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="client-detail-modal"
        className="max-w-3xl max-h-[90vh] overflow-y-auto popover-solid border border-white/10 p-0 gap-0"
      >
        <DialogTitle className="sr-only">Détail client</DialogTitle>
        <DialogDescription className="sr-only">Profil, crédits, commandes, paiements et remboursements du client.</DialogDescription>
        {loading || !d ? (
          <div className="p-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-aurora-cyan" /></div>
        ) : (
          <>
            <DialogHeader className="p-6 border-b border-white/10">
              <DialogTitle className="sr-only">Détail client {u.email}</DialogTitle>
              <div className="flex items-center gap-4">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-14 h-14 rounded-full border border-white/10" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-aurora-cyan/15 grid place-items-center text-aurora-cyan font-bold text-xl">{initials(u.name)}</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight truncate">{u.name}</h2>
                    <RoleBadge role={u.role} />
                    <AccountBadge accountType={u.account_type} vatValid={u.vat_valid} vatNumber={u.vat_number} companyName={u.company_name} />
                    <AbuseBadge user={u} />
                    {u.lang && <span data-testid="detail-lang" title="Langue utilisée par le client" className="font-mono text-[10px] px-2 py-0.5 rounded-sm border border-white/10 text-zinc-300">{langFlag(u.lang)} {langLabel(u.lang)}</span>}
                  </div>
                  <div className="font-mono text-xs text-aurora-cyan truncate" data-testid="detail-email">{u.email}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> Inscrit le {fmtDate(u.created_at)} · Code parrain {d.referral.code || "—"}
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="p-6 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                <Stat icon={Coins} label="Solde crédits" value={u.credits} color="#00E5FF" />
                <Stat icon={FileStack} label="Commandes" value={d.orders_count} color="#8B5CF6" />
                <Stat icon={Activity} label="Terminées" value={d.completed_count} color="#00E5A0" />
                <Stat icon={Euro} label="Dépensé" value={`${d.total_spent}€`} color="#00E5A0" />
                <Stat icon={Coins} label="Crédits achetés" value={d.credits_purchased} color="#FFB020" />
                <Stat icon={Gift} label="Parrainages" value={d.referral.total_referred} color="#FF6B00" />
              </div>

              {/* Actions */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Adjust credits */}
                <div className="glass rounded-md p-4">
                  <div className="font-display text-sm font-semibold uppercase tracking-tight mb-3 flex items-center gap-2">
                    <Coins className="w-4 h-4 text-aurora-cyan" /> Ajuster les crédits
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="credit-adjust-input"
                      type="number" value={creditInput} onChange={(e) => setCreditInput(e.target.value)}
                      placeholder="ex: 10 ou -5"
                      className="flex-1 min-w-0 bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-mono text-sm focus:border-aurora-cyan outline-none"
                    />
                    <button data-testid="credit-adjust-apply" onClick={adjust} disabled={busy}
                      className="shrink-0 px-5 py-2 bg-aurora-cyan/10 border border-aurora-cyan/30 text-aurora-cyan font-mono text-xs uppercase tracking-wide whitespace-nowrap rounded-sm hover:bg-aurora-cyan/20 transition-colors disabled:opacity-50">
                      Appliquer
                    </button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {[-10, -5, 5, 10, 25].map((v) => (
                      <button key={v} onClick={() => setCreditInput(String(v))}
                        className="flex-1 py-1.5 border border-white/10 rounded-sm font-mono text-[11px] text-zinc-400 hover:border-aurora-cyan/40 hover:text-aurora-cyan transition-colors">
                        {v > 0 ? `+${v}` : v}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Refund credits */}
                <div className="glass rounded-md p-4 border border-signal-success/20">
                  <div className="font-display text-sm font-semibold uppercase tracking-tight mb-3 flex items-center gap-2">
                    <RotateCcw className="w-4 h-4 text-signal-success" /> Rembourser (problème)
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="refund-credits-input"
                      type="number" min="1" value={refundCredits} onChange={(e) => setRefundCredits(e.target.value)}
                      placeholder="Crédits"
                      className="w-28 bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-mono text-sm focus:border-signal-success outline-none"
                    />
                    <input
                      data-testid="refund-reason-input"
                      value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Motif (ex: fichier non livré)"
                      className="flex-1 bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-signal-success outline-none"
                    />
                  </div>
                  <button data-testid="refund-submit" onClick={refund} disabled={busy}
                    className="mt-3 w-full py-2 bg-signal-success/10 border border-signal-success/40 text-signal-success font-mono text-xs uppercase tracking-widest rounded-sm hover:bg-signal-success/20 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Rembourser les crédits
                  </button>
                </div>
              </div>

              <button data-testid="detail-role-toggle" onClick={toggleRole} disabled={busy}
                className="flex items-center gap-2 px-3 py-2 border border-white/15 rounded-sm font-mono text-[10px] uppercase tracking-widest hover:border-aurora-purple hover:text-aurora-purple transition-colors">
                {u.role === "admin" ? <UserIcon className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                {u.role === "admin" ? "Rétrograder en client" : "Promouvoir admin"}
              </button>

              <button data-testid="detail-priority-toggle" onClick={togglePriority} disabled={busy}
                className={`mt-2 flex items-center gap-2 px-3 py-2 border rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors ${u.priority_support ? "border-aurora-cyan/50 text-aurora-cyan bg-aurora-cyan/5" : "border-white/15 hover:border-aurora-cyan hover:text-aurora-cyan"}`}>
                <Zap className="w-3.5 h-3.5" />
                {u.priority_support ? "Support Express : activé (retirer)" : "Activer le Support Express"}
              </button>

              {/* Slave user — encrypted files, manual processing */}
              <div data-testid="slave-config" className={`glass rounded-md p-4 ${u.is_slave ? "border border-amber-400/40" : "border border-white/10"}`}>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="font-display text-sm font-semibold uppercase tracking-tight flex items-center gap-2">
                    <Lock className={`w-4 h-4 ${u.is_slave ? "text-amber-400" : "text-zinc-400"}`} /> Utilisateur Slave (fichiers cryptés)
                  </div>
                  <button data-testid="slave-toggle" onClick={toggleSlave} disabled={busy}
                    className={`shrink-0 px-3 py-1.5 border rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors ${u.is_slave ? "border-amber-400/50 text-amber-400 bg-amber-400/10" : "border-white/15 hover:border-amber-400 hover:text-amber-400"}`}>
                    {u.is_slave ? "Activé (désactiver)" : "Activer"}
                  </button>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 mb-3">
                  Les fichiers de ce client sont cryptés (Dimsport / Flex / Autotuner). Ils contournent la reconnaissance binaire et vont directement en file de traitement MANUEL.
                </p>
                {u.is_slave && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Outil</label>
                      <select data-testid="slave-tool-select" value={slaveTool} onChange={(e) => setSlaveTool(e.target.value)}
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-amber-400 outline-none">
                        <option value="dimsport">Dimsport</option>
                        <option value="flex">Flex (Magic Motorsport)</option>
                        <option value="autotuner">Autotuner</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Master (optionnel)</label>
                      <input data-testid="slave-master-input" value={masterLabel} onChange={(e) => setMasterLabel(e.target.value)}
                        placeholder="ex: ID / nom du master"
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-amber-400 outline-none" />
                    </div>
                    <button data-testid="slave-save" onClick={saveSlaveConfig} disabled={busy}
                      className="sm:col-span-2 py-2 bg-amber-400/10 border border-amber-400/40 text-amber-400 font-mono text-xs uppercase tracking-widest rounded-sm hover:bg-amber-400/20 transition-colors disabled:opacity-50">
                      Enregistrer la configuration Slave
                    </button>
                  </div>
                )}
              </div>

              {/* Orders */}
              <Section title="Commandes" icon={FileStack} count={d.orders.length}>
                {d.orders.length === 0 ? <Empty /> : d.orders.slice(0, 12).map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                    <div className="min-w-0">
                      <div className="font-display uppercase tracking-tight text-sm truncate">{o.vehicle || "Véhicule"}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{fmtDate(o.created_at)} · {o.total_credits} cr. · {o.services.join(", ")}</div>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                ))}
              </Section>

              {/* Payments */}
              <Section title="Paiements" icon={Receipt} count={d.transactions.length}>
                {d.transactions.length === 0 ? <Empty /> : d.transactions.slice(0, 12).map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 py-2.5 border-b border-white/5 last:border-0">
                    <div className="min-w-0">
                      <div className="font-mono text-sm">{t.amount}{(t.currency || "eur").toUpperCase() === "EUR" ? "€" : ` ${(t.currency || "").toUpperCase()}`} · {t.credits} cr.</div>
                      <div className="font-mono text-[10px] text-zinc-500">{fmtDate(t.created_at)} · {t.package_id}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${t.credited ? "border-signal-success/40 text-signal-success" : t.payment_status === "paid" ? "border-aurora-cyan/40 text-aurora-cyan" : "border-white/15 text-zinc-500"}`}>
                        {t.credited ? "Crédité" : t.payment_status || "—"}
                      </span>
                      {t.stripe_refunded ? (
                        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-signal-error/40 text-signal-error">Remboursé €</span>
                      ) : t.payment_status === "paid" ? (
                        <button
                          data-testid={`stripe-refund-${t.session_id}`}
                          onClick={() => stripeRefund(t.session_id, t.amount)} disabled={busy}
                          className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border border-signal-error/30 text-signal-error hover:bg-signal-error hover:text-white transition-colors disabled:opacity-50"
                        >
                          Rembourser €
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </Section>

              {/* Refunds */}
              {d.refunds.length > 0 && (
                <Section title="Remboursements" icon={RotateCcw} count={d.refunds.length}>
                  {d.refunds.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                      <div className="min-w-0">
                        <div className="font-mono text-sm text-signal-success">+{r.credits} cr.</div>
                        <div className="font-mono text-[10px] text-zinc-500 truncate">{fmtDate(r.at)} · {r.reason || "—"} · par {r.by}</div>
                      </div>
                    </div>
                  ))}
                </Section>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, icon: Icon, count, children }) => (
  <div>
    <div className="font-display text-sm font-semibold uppercase tracking-tight mb-2 flex items-center gap-2">
      <Icon className="w-4 h-4 text-zinc-400" /> {title}
      <span className="font-mono text-[10px] text-zinc-600">({count})</span>
    </div>
    <div className="glass rounded-md px-4">{children}</div>
  </div>
);

const Empty = () => <p className="text-zinc-500 text-sm py-3">Aucune donnée.</p>;

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const adjust = async (e, user_id, delta) => {
    e.stopPropagation();
    await api.patch(`/admin/users/${user_id}`, { credits_delta: delta });
    toast.success(`${delta > 0 ? "+" : ""}${delta} crédits`);
    load();
  };

  const totals = useMemo(() => ({
    clients: users.length,
    credits: users.reduce((s, u) => s + (u.credits || 0), 0),
    revenue: users.reduce((s, u) => s + (u.total_spent || 0), 0).toFixed(2),
    active: users.filter((u) => (u.orders_count || 0) > 0).length,
  }), [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, query]);

  return (
    <AppShell mode="admin">
      <div className="mb-6">
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// Gestion</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">Clients</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={UsersIcon} label="Clients" value={totals.clients} color="#00E5FF" />
        <Stat icon={Coins} label="Crédits en circulation" value={totals.credits} color="#FFB020" />
        <Stat icon={Euro} label="CA total" value={`${totals.revenue}€`} color="#00E5A0" />
        <Stat icon={Activity} label="Clients actifs" value={totals.active} color="#8B5CF6" />
      </div>

      <ClientWorldMap />

      <LinkedAccountsPanel onChanged={load} />


      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid="user-search"
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher par nom ou email..."
          className="w-full bg-black/40 border border-white/15 rounded-sm pl-10 pr-3 py-2.5 text-sm focus:border-aurora-cyan outline-none"
        />
      </div>

      <div className="glass rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="col-span-4">Utilisateur</div>
          <div className="col-span-1">Rôle</div>
          <div className="col-span-2">Crédits</div>
          <div className="col-span-1">Cmd.</div>
          <div className="col-span-2">Dépensé</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 py-12 text-center">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="font-mono text-xs text-zinc-500 py-12 text-center">Aucun client.</div>
        ) : (
          filtered.map((u) => (
            <div
              key={u.user_id}
              data-testid={`user-row-${u.user_id}`}
              onClick={() => setSelected(u.user_id)}
              className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 last:border-0 items-center cursor-pointer hover:bg-white/5 transition-colors"
            >
              <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-aurora-cyan/20 grid place-items-center text-aurora-cyan font-bold text-sm">{initials(u.name)}</div>}
                <div className="min-w-0">
                  <div className="text-sm truncate flex items-center gap-2">{u.name}<AccountBadge accountType={u.account_type} vatValid={u.vat_valid} vatNumber={u.vat_number} companyName={u.company_name} /><AbuseBadge user={u} />{u.lang && <span data-testid={`user-lang-${u.user_id}`} title={`Langue : ${langLabel(u.lang)}`} className="font-mono text-[9px] text-zinc-400 shrink-0">{langFlag(u.lang)} {u.lang.toUpperCase()}</span>}</div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">{u.email}</div>
                </div>
              </div>
              <div className="md:col-span-1"><RoleBadge role={u.role} /></div>
              <div className="md:col-span-2 font-mono text-aurora-cyan font-bold">{u.credits} cr.</div>
              <div className="md:col-span-1 font-mono text-sm text-zinc-300">{u.orders_count ?? 0}</div>
              <div className="md:col-span-2 font-mono text-sm text-signal-success">{u.total_spent ?? 0}€</div>
              <div className="md:col-span-2 flex items-center justify-end gap-2">
                <button data-testid={`credit-minus-${u.user_id}`} onClick={(e) => adjust(e, u.user_id, -5)} className="w-8 h-8 grid place-items-center border border-white/15 rounded-sm hover:border-signal-error hover:text-signal-error transition-colors"><Minus className="w-4 h-4" /></button>
                <button data-testid={`credit-plus-${u.user_id}`} onClick={(e) => adjust(e, u.user_id, 5)} className="w-8 h-8 grid place-items-center border border-white/15 rounded-sm hover:border-signal-success hover:text-signal-success transition-colors"><Plus className="w-4 h-4" /></button>
                <button data-testid={`detail-${u.user_id}`} onClick={(e) => { e.stopPropagation(); setSelected(u.user_id); }} className="px-3 h-8 border border-white/15 rounded-sm font-mono text-[10px] uppercase tracking-widest hover:border-aurora-cyan hover:text-aurora-cyan transition-colors">
                  Détail
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ClientDetailModal
        userId={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onChanged={load}
      />
    </AppShell>
  );
};
